import { useMemo } from 'react';

import { DayTag } from '@/components/DayTag';
import { PrintIcon } from '@/components/Icons';
import { Paragraph } from '@/components/Paragraph';
import { useDeviceBreakpoints } from '@/hooks/useDeviceBreakpoints';
import { StyledFlexColumn, StyledFlexRow } from '@/layout/FlexLayout';
import { ItineraryProductDayToDay } from '@/pages/DayToDay/components/ItineraryProductDayToDay';
import {
  StyledDotPlacement,
  StyledDotBackground,
  StyledDot,
  StyledItineraryBlockCard,
  StyledItineraryBlockContainer,
  StyledItineraryBlockBackground,
  StyledPrintButton,
} from '@/pages/DayToDay/styles';
import { createDateDurationString } from '@/utils/createDateDurationString';
import { createItineraryBlockDayString } from '@/utils/createItineraryBlockDayString';

import type { ItineraryBlock } from '@/types/itinerary';

interface ItineraryBlockProps {
  itineraryBlock: ItineraryBlock;
  index: number;
  isFirstBlock?: boolean;
  isLastBlock?: boolean;
  nextBlockTitle?: ItineraryBlock | false;
}

export function ItineraryBlockDayToDay({
  itineraryBlock,
  index,
  isLastBlock,
  isFirstBlock,
  nextBlockTitle,
}: ItineraryBlockProps) {
  const { isTabletOrSmaller, isMobile } = useDeviceBreakpoints();
  const dayString = useMemo(() => createItineraryBlockDayString(itineraryBlock), [itineraryBlock]);
  const dateString = useMemo(
    () => createDateDurationString(itineraryBlock.startDate, itineraryBlock.endDate),
    [itineraryBlock],
  );

  const showBackground = index % 2 !== 0;

  return (
    <>
      {isFirstBlock && nextBlockTitle && (
        <StyledFlexColumn alignItems="center" paddingBottom={10}>
          <StyledFlexRow marginLeft="auto">
            <PrintIcon />

            <StyledPrintButton type="button" onClick={() => window.print()}>
              Reisschema afdrukken
            </StyledPrintButton>
          </StyledFlexRow>
          <DayTag startDay={nextBlockTitle.startDay} endDay={nextBlockTitle.endDay} />
          <h1>{nextBlockTitle.name} </h1>
        </StyledFlexColumn>
      )}

      <StyledItineraryBlockContainer
        $dashed={itineraryBlock.type === 'connection'}
        $showBorder={!isLastBlock}
        id={`program-section-${itineraryBlock.id}`}
      >
        {showBackground && <StyledItineraryBlockBackground $fullWidth={isMobile} />}
        <StyledDotPlacement $isOnBackground={showBackground}>
          <StyledDot />
          <StyledDotBackground />
        </StyledDotPlacement>
        <StyledItineraryBlockCard flexDirection={isTabletOrSmaller ? 'column' : 'row'}>
          <StyledFlexColumn flex="1">
            <h2>{dayString}</h2>
            <Paragraph weight={600} size="xl">
              {dateString}
            </Paragraph>
          </StyledFlexColumn>
          <StyledFlexColumn gap={7} flex="2">
            {itineraryBlock.ItineraryProducts.map((product) => (
              <ItineraryProductDayToDay itineraryProduct={product} key={product.id} />
            ))}
          </StyledFlexColumn>
        </StyledItineraryBlockCard>

        {nextBlockTitle && !isFirstBlock && (
          <StyledFlexColumn alignItems="center" paddingBottom={10}>
            <DayTag startDay={nextBlockTitle.startDay} endDay={nextBlockTitle.endDay} />
            <h1>{nextBlockTitle.name} </h1>
          </StyledFlexColumn>
        )}
      </StyledItineraryBlockContainer>
    </>
  );
}
